import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";

import ForgotPassword from "components/ForgotPassword/index";
import Homepage from "containers/Homepage/index";
import UpgradePlan from "containers/UpgradePlan/index";
import SignUp from "containers/SignUpContainer/index";
import SignInContainer from "containers/SignInContainer/index";
import SignUpHeader from "components/SignUpHeader/index";
import UpgradePaymentPage from "containers/UpgradePaymentPage/index";
import CreatePasswordContainer from "containers/CreatePasswordContainer/CreatePasswordContainer";
import ResetPasswordContainer from "containers/ResetPasswordContainer/ResetPasswordContainer";
import ProtectedRoute from "Route/ProtectedRoute";
import "./styles/global.scss";
import { useLayoutEffect, useRef } from "react";
// import Marquee from "components/Marquee/index";
import { sdkInit, importNavigateFn, getPin, getEmail, createSignInRequestForExternalUser } from "@induz/sdk";
import { useToast } from "components/Toaster/index";
import { useNavigate } from "react-router-dom";
import MigrateMessage from "containers/MigrateMessage/migrateMessage";
import ExternalShare from "containers/ExternalShare/ExternalShare";
import Maintenance from "containers/Maintenance/index";
import { cleanup } from "@induz/sdk";
import { getSignInRequestForExternalUser } from "../node_modules/@induz/sdk/dist/index";

function App() {
  const showToast = useToast();
  // Inactivity management (15 minutes)
  const INACTIVITY_LIMIT = 15 * 60 * 1000;
  const idleTimerRef = useRef(null);
  const operationActiveCountRef = useRef(0);

  const handleIdleTimeout = () => {
    const path = window.location.pathname;
    const isAuthPage =
      path === "/signin" ||
      path === "/signup" ||
      path === "/forgotpassword" ||
      path === "/resetpassword" ||
      path === "/createpassword";

    if (isAuthPage) {
      return;
    }

    console.warn("[App] Inactivity limit reached. Redirecting to sign-in with PIN...");
    cleanup();
    window.location.assign("/signin");
  };

  const resetTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    // Don't start idle timer while upload/download operations are active
    if (operationActiveCountRef.current > 0) return;
    idleTimerRef.current = setTimeout(handleIdleTimeout, INACTIVITY_LIMIT);
  };

  useLayoutEffect(() => {
    // Activity listeners
    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const handleActivity = () => resetTimer();

    // Track active upload/download operations to suppress idle timer
    const handleOperationStart = () => {
      operationActiveCountRef.current += 1;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
    const handleOperationEnd = () => {
      operationActiveCountRef.current = Math.max(0, operationActiveCountRef.current - 1);
      if (operationActiveCountRef.current === 0) {
        resetTimer();
      }
    };

    window.addEventListener("operationStart", handleOperationStart);
    window.addEventListener("operationEnd", handleOperationEnd);
    activityEvents.forEach(name => document.addEventListener(name, handleActivity));
    resetTimer();

    const onStart = async () => {
      sessionStorage.removeItem("mail");
      console.warn("Initializing SDK...");
      // Cap the pool at 4 (default is 8). Browser per-host connection limits are
      // shared across all tabs to the same origin, and Safari's ceiling is low;
      // a smaller footprint here leaves room for a yoffice editor tab opened from
      // ysafeUI without exhausting the shared budget.
      await sdkInit({ poolSize: 4 });
      console.warn("SDK initialized successfully.");
    };

    // Global listener for editor handshake
    const handleHandshake = (event) => {
      // console.log("[App] Received postMessage from origin:", event.origin, "Data:", event.data);

      // Allow localhost for development and the production domain
      const isAllowed = event.origin === window.origin ||
        event.origin.startsWith("http://localhost") ||
        event.origin === "https://ysafe.io";

      if (!isAllowed) {
        console.warn("[App] Handshake rejected: Origin not allowed");
        return;
      }

      if (event.data?.type === "REQUEST_CREDENTIALS") {
        console.log("[App] Processing REQUEST_CREDENTIALS...");
        const token = localStorage.getItem("izauthtoken");
        const pin = getPin();
        const email = getEmail();
        const externalSignInRequest = getSignInRequestForExternalUser();


        console.log("[App] Found credentials - Token:", !!token, "PIN:", !!pin, "Email:", !!email, "External SignIn Request:", !!externalSignInRequest);
        if (externalSignInRequest) {
          console.log("[App] Sending externalSignInRequest to source...");
          event.source.postMessage({
            type: "SEND_CREDENTIALS",
            payload: { externalSignInRequest }
          }, event.origin);
        }
        else if (token && pin && email) {
          console.log("[App] Sending SEND_CREDENTIALS to source...");
          event.source.postMessage({
            type: "SEND_CREDENTIALS",
            payload: { token, pin, email }
          }, event.origin);
        }
        else {
          console.warn("[App] Missing credentials, cannot fulfill request. Manual login might be required.");
        }
      }
    };

    window.addEventListener("message", handleHandshake);

    // Global listener for cross-tab local storage changes (e.g. login/logout in another tab)
    const handleStorageChange = (event) => {
      if (event.key === "izauthtoken") {
        // console.log("[App] izauthtoken changed in another tab. Reloading window...");
        window.location.reload();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    try {
      onStart();
    } catch (error) {
      console.error("SDK initialization failed:", error);
      if (window.location.pathname !== "/maintenance") {
        window.location.href = "/maintenance";
      }
    }

    return () => {
      window.removeEventListener("message", handleHandshake);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("operationStart", handleOperationStart);
      window.removeEventListener("operationEnd", handleOperationEnd);
      activityEvents.forEach(name => document.removeEventListener(name, handleActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);
  return (
    <Router>
      <MainContent />
    </Router>
  );
}

function MainContent() {
  const location = useLocation();
  const isHeaderVisible =
    location.pathname !== "/homepage" &&
    location.pathname !== "/maintenance" &&
    location.pathname !== "/access_external_share"; // Hide header on maintenance, homepage & external share
  const navigate = useNavigate();
  importNavigateFn(navigate);

  return (
    <div className="appContainerr">
      {isHeaderVisible && <Header />}

      <div
        className={`contentContainer ${isHeaderVisible ? "with-header" : ""}`}
      >
        <Routes>
          <Route path="/" element={<Navigate to="/signin" />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/signin" element={<SignInContainer />} />
          <Route path="/createpassword" element={<CreatePasswordContainer invite={true} />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/resetpassword" element={<ResetPasswordContainer />} />
          <Route path="/migrate" element={<MigrateMessage />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/access_external_share" element={<ExternalShare />} />
          <Route
            path="/homepage"
            element={<ProtectedRoute element={<Homepage />} />}
          // element={<Homepage />}
          />
          <Route
            path="/upgrade-plan"
            element={<ProtectedRoute element={<UpgradePlan />} />}
          />
          <Route
            path="/payment"
            element={<ProtectedRoute element={<UpgradePaymentPage />} />}
          />
        </Routes>

      </div>
      {/* <Marquee speed={15}>
        <span style={{ color: "red", fontWeight: "bold" }}>
          Disclaimer: This is the beta release.
        </span>
      </Marquee> */}
    </div>
  );
}

const Header = () => (
  <div className="headerContainer">
    <SignUpHeader />
  </div>
);

export default App;
